import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getStoredUser } from "../api/api.js";

function getInitialTheme() {
  try {
    return localStorage.getItem("theme") || "light";
  } catch {
    return "light";
  }
}

function applyTheme(theme) {
  try {
    const root = document.documentElement;
    const body = document.body;

    root.setAttribute("data-theme", theme);

    if (theme === "dark") {
      root.classList.add("dark");
      body?.classList.add("dark");
    } else {
      root.classList.remove("dark");
      body?.classList.remove("dark");
    }

    localStorage.setItem("theme", theme);
  } catch {}
}

export default function AppShell({ title, subtitle, right, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const u = getStoredUser();

    if (!u) {
      navigate("/login");
      return;
    }

    setUser(u);
  }, [navigate]);

  useEffect(() => {
    function onSessionExpired() {
      const from = location.pathname + location.search;
      navigate(`/login?from=${encodeURIComponent(from)}`, { replace: true });
    }
    window.addEventListener("agro:session-expired", onSessionExpired);
    return () => window.removeEventListener("agro:session-expired", onSessionExpired);
  }, [navigate, location]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;

    if (String(user.role || "").toUpperCase() === "WORKER") {
      const allowed = ["/main", "/tasks"];
      const ok = allowed.some(
        (path) =>
          location.pathname === path ||
          location.pathname.startsWith(path + "/")
      );

      if (!ok) {
        navigate("/main", { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  const menu = useMemo(() => {
    const role = String(user?.role || "").toUpperCase();

    if (role === "WORKER") {
      return [
        { key: "home", label: t("nav.home"), path: "/main", icon: "home" },
        { key: "tasks", label: t("nav.tasks"), path: "/tasks", icon: "check" }
      ];
    }

    const base = [
      { key: "home", label: t("nav.home"), path: "/main", icon: "home" },
      { key: "dashboard", label: t("nav.dashboard"), path: "/dashboard", icon: "dashboard" },
      { key: "fields",  label: t("nav.fields"),  path: "/fields",  icon: "leaf"  },
      { key: "yield",   label: "Врожайність",    path: "/yield",   icon: "grain" },
      { key: "ndvi",    label: "NDVI",           path: "/ndvi",    icon: "ndvi"  },
      // { key: "workers", label: t("nav.workers"), path: "/workers", icon: "users" },
      { key: "tasks",   label: t("nav.tasks"),   path: "/tasks",   icon: "check" }
    ];
    return base;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const isDark = theme === "dark";
  const orgName = (user?.organizationName || user?.organization?.name || "").trim() || t("sidebar.org");
  const firstLetter = user?.name?.trim()?.[0]?.toUpperCase() || "U";

  function formatRole(role) {
    switch (String(role || "").toUpperCase()) {
      case "MANAGER":     return "Менеджер";
      case "AGRONOMIST":  return "Агроном";
      case "WORKER":      return "Працівник";
      case "ADMIN":       return "Адміністратор";
      default:            return role || "Користувач";
    }
  }

  const isFieldsPage =
    location.pathname === "/fields" || location.pathname.startsWith("/fields/");

  function go(path) {
    navigate(path);
  }

  function logout() {
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    navigate("/login");
  }

  function toggleTheme() {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }

  if (!user) return null;

  return (
    <div className="app">
      <div
        className={`overlay ${mobileMenuOpen ? "show" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <aside className={`sidebar ${mobileMenuOpen ? "open" : ""}`}>
        <div className="brand" onClick={() => go("/main")} role="button" tabIndex={0}>
          <div className="brandLogo">A</div>

          <div>
            <div className="brandTitle">AgroVision</div>
            <div className="brandSub">{t("brand.subtitle")}</div>
          </div>

          <button
            className="iconBtn closeBtn"
            onClick={(e) => {
              e.stopPropagation();
              setMobileMenuOpen(false);
            }}
            aria-label={t("sidebar.closeMenu")}
            type="button"
          >
            ✕
          </button>
        </div>

        <nav className="nav">
          {menu.map((item) => {
            const active =
              location.pathname === item.path ||
              (item.path !== "/main" && location.pathname.startsWith(item.path));

            return (
              <button
                key={item.key}
                className={`navItem ${active ? "active" : ""}`}
                onClick={() => go(item.path)}
                type="button"
              >
                <span className="navIcon">{getIcon(item.icon)}</span>
                <span className="navLabel">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebarFooter">
          <div className="orgBox">
            <div className="orgTitle">{t("sidebar.org")}</div>
            <div className="orgValue">{orgName}</div>
          </div>

          <button className="btn danger" onClick={logout} type="button">
            {t("auth.logout")}
          </button>
        </div>
      </aside>

      <main className="content">
        <div className="mobileTopbar">
          <div
            style={{ fontWeight: 950, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 17 }}
            onClick={() => go("/main")}
            role="button"
            tabIndex={0}
          >
            AgroVision
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button className="btn" onClick={toggleTheme} type="button" style={{ padding: "6px 10px", minHeight: 38 }} title={t("theme.toggle")}>
              {isDark ? "☀️" : "🌙"}
            </button>
            <div className="avatar" style={{ width: 36, height: 36, fontSize: 15 }}>
              {firstLetter}
            </div>
          </div>
        </div>

        <header className="topbar">
          {/* hamburger — only visible at 761–900px where sidebar is a drawer but bottom-nav is absent */}
          <button
            type="button"
            className="btn tabletHamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Відкрити меню"
          >
            ☰
          </button>

          <div style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
            <div className="pageTitle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {title || "AgroVision"}
            </div>
            <div className="pageHint">{subtitle || t("auth.welcome", { name: user.name })}</div>
          </div>

          <div className="topbarRight">
            {!isFieldsPage && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button className="btn" onClick={toggleTheme} type="button" title={t("theme.toggle")}>
                    {isDark ? t("theme.light") : t("theme.dark")}
                  </button>
                  {right}
                </div>

                <div className="topbarSep" />

                <div className="profile">
                  <div className="avatar">{firstLetter}</div>
                  <div>
                    <div className="profileName">{user?.name || t("common.noName")}</div>
                    <div className="profileMeta">
                      {orgName} · {formatRole(user?.role)}
                    </div>
                  </div>
                </div>
              </>
            )}

            {isFieldsPage && right}
          </div>
        </header>

        <div className="contentScroll">{children}</div>
      </main>

      {/* Mobile bottom navigation — only visible at ≤760px via CSS */}
      <nav className="bottomNav">
        {menu.map((item) => {
          const active =
            location.pathname === item.path ||
            (item.path !== "/main" && location.pathname.startsWith(item.path));
          return (
            <button
              key={item.key}
              className={`bottomNavItem ${active ? "active" : ""}`}
              onClick={() => go(item.path)}
              type="button"
            >
              <span className="bottomNavIcon">{getIcon(item.icon)}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function getIcon(name) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none"
  };

  const stroke = {
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  };

  switch (name) {
    case "home":
      return (
        <svg {...common}>
          <path {...stroke} d="M3 11l9-8 9 8" />
          <path {...stroke} d="M5 10v10h14V10" />
        </svg>
      );

    case "dashboard":
      return (
        <svg {...common}>
          <path {...stroke} d="M4 13h7V4H4v9Z" />
          <path {...stroke} d="M13 20h7V11h-7v9Z" />
          <path {...stroke} d="M13 4h7v5h-7V4Z" />
          <path {...stroke} d="M4 15h7v5H4v-5Z" />
        </svg>
      );

    case "leaf":
      return (
        <svg {...common}>
          <path
            {...stroke}
            d="M20 4c-7 0-12 5-12 12 0 2.5 1.5 4 4 4 7 0 12-5 12-12 0-2.5-1.5-4-4-4Z"
          />
          <path {...stroke} d="M8 16c2-2 5-5 12-8" />
        </svg>
      );

    case "users":
      return (
        <svg {...common}>
          <path {...stroke} d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <path {...stroke} d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
        </svg>
      );

    case "check":
      return (
        <svg {...common}>
          <path {...stroke} d="M20 6L9 17l-5-5" />
        </svg>
      );

    case "grain":
      return (
        <svg {...common}>
          <path {...stroke} d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7Z" />
          <circle cx="12" cy="9" r="2.5" {...stroke} />
        </svg>
      );

    case "ndvi":
      return (
        <svg {...common}>
          <path {...stroke} d="M12 22V12" />
          <path {...stroke} d="M12 12C12 7 7 3 3 3c0 5 4 9 9 9Z" />
          <path {...stroke} d="M12 12c0-5 5-9 9-9-1 5-4 9-9 9Z" />
        </svg>
      );

    default:
      return null;
  }
}
